import React, { useEffect } from 'react';
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router';
import * as Sentry from '@sentry/react';
import { Box, Typography, Button, Paper, Stack } from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  CloudOff as CloudOffIcon,
} from '@mui/icons-material';

/**
 * Detect whether an error is a chunk/dynamic-import load failure.
 *
 * These occur when the browser cannot download a lazy-loaded JS chunk,
 * typically due to a network interruption or a deployment that
 * invalidated old chunk hashes.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  // Webpack emits errors with name "ChunkLoadError"
  if (error.name === 'ChunkLoadError') return true;

  const msg = error.message.toLowerCase();
  return (
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed')
  );
}

/**
 * Route-level error boundary for React Router.
 *
 * Catches rendering errors, loader failures, and thrown responses
 * from any child route and displays a user-friendly fallback UI
 * instead of the default React Router developer error screen.
 *
 * Chunk-load failures (e.g. network errors while downloading a
 * lazy-loaded route) receive a dedicated UI with a simple retry
 * button that reloads the page.
 */
export const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  // Report to Sentry (no-op when Sentry is disabled — no DSN). Skip 404s: an unmatched route is an
  // expected outcome (often bot traffic), not a fault, so capturing it would only burn quota.
  //
  // Chunk-load failures are EXPECTED after every deployment (the new image carries only the new
  // hashed assets, so a tab still on the old index page misses the next lazy route). lazyWithRetry
  // self-heals them with a reload; the ones that reach this boundary already have a friendly retry
  // card. Report them as a single warning-level issue so they stay visible as a deploy-churn
  // signal without paging as errors.
  useEffect(() => {
    if (isRouteErrorResponse(error) && error.status === 404) return;
    if (isChunkLoadError(error)) {
      Sentry.captureException(error, {
        level: 'warning',
        tags: { chunk_load: 'true' },
        fingerprint: ['chunk-load-error'],
      });
      return;
    }
    Sentry.captureException(error);
  }, [error]);

  // ---- Chunk load failure ------------------------------------------------
  if (isChunkLoadError(error)) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          p: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 480,
            width: '100%',
            textAlign: 'center',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <CloudOffIcon color="warning" sx={{ fontSize: 56, mb: 2 }} />

          <Typography variant="h5" gutterBottom>
            Failed to load page
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            A required resource could not be downloaded. This is usually caused
            by a network issue or an application update. Please retry.
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // ---- Generic error handling --------------------------------------------
  let title = 'Something went wrong';
  let message = 'An unexpected error occurred. Please try again.';
  let details: string | undefined;

  if (isRouteErrorResponse(error)) {
    title = `${error.status} ${error.statusText}`;
    message =
      error.status === 404
        ? 'The page you are looking for does not exist.'
        : typeof error.data === 'string'
          ? error.data
          : 'The server returned an error.';
  } else if (error instanceof Error) {
    title = error.name || 'Error';
    message = error.message;
    details = import.meta.env.DEV ? error.stack : undefined;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          maxWidth: 520,
          width: '100%',
          textAlign: 'center',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <ErrorIcon color="error" sx={{ fontSize: 56, mb: 2 }} />

        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {message}
        </Typography>

        {details && (
          <Paper
            variant="outlined"
            sx={(theme) => ({
              p: 2,
              mb: 3,
              textAlign: 'left',
              maxHeight: 200,
              overflow: 'auto',
              bgcolor: 'grey.50',
              ...theme.applyStyles('dark', { bgcolor: 'grey.800' }),
            })}
          >
            <Typography
              variant="caption"
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}
            >
              {details}
            </Typography>
          </Paper>
        )}

        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            Reload Page
          </Button>
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default RouteErrorBoundary;
